/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * 缓存实现
 * 每隔[cacheDelay]秒缓存一次
 * 每次缓存时，将当前步骤数据取出，合并到一个对象里（该对象格式与提交接口所需格式一致），并将该对象缓存在localstorage
 * 每次组件生命周期created执行时，将缓存对象中与本步骤数据对应字段到值取出进行初始化
 * 完成时，清除缓存
 * 约定：
 * 1.需要缓存数据的组件，须将要缓存的数据放在一个非空对象里维护
 * 2.对于需要预设值的组件，需设置ready标记（默认字段ready），使用缓存数据前，会等待ready为true
 * 3.缓存恢复后会$emit一个cacheRecovered事件，参数为恢复的数据引用
 */
import {
  getItem,
  setItem,
  removeItem,
} from "@/service/common/storage/localStorage";
import Vue from "vue";
/** 缓存关键字(默认) */
const cacheKey = "_joinUsData";
/** 缓存延时(默认) */
const cacheDelay = 1000;
/** ready检测延时(默认) */
const checkReadyDelay = 1000;
/** 是否进行缓存 用于控制是否自动进行缓存*/
let wouldSaving = true;
/** 窗口是否聚焦 非聚焦时不缓存 */
let windowFocusing = true;

window.addEventListener("blur", () => {
  windowFocusing = false;
});
window.addEventListener("focus", () => {
  windowFocusing = true;
});
/**
 * 检测是否就绪，主要针对需要预设值的组件
 * @param ctx 组件上下文
 * @param config 缓存配置
 * @returns
 */
async function checkReady(ctx: any, config: CacheMixinConfig) {
  return new Promise((resolve) => {
    const readyField = config.readyField;
    if (readyField && typeof ctx[readyField] === "boolean") {
      const check = () => {
        if (ctx[readyField]) {
          resolve(true);
        } else {
          setTimeout(() => {
            check();
          }, config.checkReadyDelay || checkReadyDelay);
        }
      };
      setTimeout(() => {
        //异步执行，避免可能的第一次等待
        check();
      });
    } else {
      resolve(true);
    }
  });
}
/**
 * 使用缓存数据设置组件form对象的值
 * @param ctx 组件上下文
 * @param config 缓存配置
 * @returns
 */
function recoverData(ctx: any, config: CacheMixinConfig) {
  if (typeof config.useCache === "function") {
    const use = config.useCache(ctx);
    if (!use) {
      return;
    }
  }
  const dataField = config.field;
  const dataRef = ctx[dataField];
  const cacheDataMap = getItem(cacheKey) || {};
  if (dataRef && typeof dataRef === "object") {
    const cacheData = cacheDataMap[config.saveKey];
    if (cacheData && typeof cacheData === "object") {
      Object.keys(cacheData).forEach((key) => {
        Vue.set(dataRef, key, cacheData[key]);
      });
      ctx.$emit("cacheRecovered", dataRef);
      if (typeof config.cacheRecovered === "function") {
        config.cacheRecovered(ctx, dataRef);
      }
    }
  }
}
/**
 * 循环缓存
 * @param ctx 组件上下文
 * @param config 缓存配置
 */
function saveToCache(ctx: any, config: CacheMixinConfig) {
  if (!wouldSaving) {
    return;
  }
  const save = () => {
    if (ctx.cacheable && windowFocusing) {
      const dataField = config.field;
      const dataRef = ctx[dataField];
      const cacheDataMap = getItem(cacheKey) || {};
      if (dataRef) {
        let data = cacheDataMap[config.saveKey] || {};
        data = Object.assign(data, dataRef);
        cacheDataMap[config.saveKey] = data;
        setItem(cacheKey, cacheDataMap);
      }
    }
    setTimeout(() => {
      if (!wouldSaving) {
        return;
      }
      save();
    }, config.cacheDelay || cacheDelay);
  };
  setTimeout(() => {
    save();
  }, config.cacheDelay || cacheDelay);
}
/**
 * 移除缓存
 */
export function removeCache() {
  removeItem(cacheKey);
}
/**
 * 停止进行缓存
 */
export function stopSaving() {
  wouldSaving = false;
}
/**
 * 启用缓存
 */
export function startSaving() {
  wouldSaving = true;
}
export interface CacheMixinConfig {
  /** 缓存的数据字段 */
  field: string;
  /** 缓存关键字 */
  saveKey: string;
  /** 是否就绪字段，默认ready */
  readyField?: string;
  /** 缓存延时（单位：ms；等于0时使用默认值） */
  cacheDelay?: number;
  /** 检测延时（单位：ms；等于0时使用默认值） */
  checkReadyDelay?: number;
  cacheRecovered?: (ctx: any, data: any) => void;
  /** 是否使用缓存 */
  useCache?: (ctx: any) => boolean;
}
export function genCacheMixin(config: CacheMixinConfig) {
  return {
    props: {
      cacheable: {
        type: Boolean,
        default: true,
      },
    },
    async mounted() {
      await checkReady(this, config);
      recoverData(this, config);
      saveToCache(this, config);
    },
  };
}
